<script>
  import { onDestroy, onMount, tick } from 'svelte';
  import Portal from 'svelte-portal';
  import {
    ChevronLeft,
    ChevronRight,
    Download,
    FileText,
    Heart,
    PanelRightClose,
    PanelRightOpen,
    Play,
    RotateCw,
    Share2,
    Video,
    X,
    ZoomIn,
    ZoomOut
  } from 'lucide-svelte';
  import VideoPreview from './VideoPreview.svelte';
  import { sendMessage } from '$lib/websocket-client';
  import { getMediaUrlHints, isImage, isPDF, isVideo, normalizeUrl, preferInlineMediaUrl } from '$lib/utils';

  export let messages = [];
  export let initialMessageId = null;
  export let initialAlbumIndex = 0;
  export let onClose = () => {};

  let index = 0;
  let strip;
  let imageEl;
  let stageEl;
  let panelVisible = true;
  let scale = 1;
  let rotation = 0;
  let rotationFit = 1;
  let panX = 0;
  let panY = 0;
  let activePointers = new Map();
  let pinchDistance = 0;
  let pinchScale = 1;
  let lastPointer = null;
  let zoomFocusPoint = null;
  let likeOverrides = {};
  let notice = '';
  let noticeTimer;
  let previousOverflow = '';
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;

  function albumUrls(message) {
    try {
      const value = typeof message.message === 'string' ? JSON.parse(message.message) : message.message;
      return Array.isArray(value?.fullUrls) && value.fullUrls.length ? value.fullUrls : value?.urls || [];
    } catch {
      return [];
    }
  }

  $: entries = (Array.isArray(messages) ? messages : []).flatMap((message) => {
    if (message?.type === 'album') {
      return albumUrls(message).map((url, albumIndex) => ({
        key: `${message.id}:${albumIndex}`,
        messageId: message.id,
        albumIndex,
        url: preferInlineMediaUrl(url, 'image/'),
        name: `Ảnh ${albumIndex + 1}`,
        type: 'image',
        sender: message.fromName || message.sender_name || '',
        date: message.created_at || message.time || ''
      }));
    }
    if (message?.type !== 'media') return [];
    const rawUrl = message.media?.url || message.message;
    if (typeof rawUrl !== 'string' || !rawUrl) return [];
    const hints = getMediaUrlHints(rawUrl);
    const mime = message.media?.mime || message.meta?.mime || hints.mime || '';
    const url = preferInlineMediaUrl(rawUrl, mime);
    return [
      {
        key: String(message.id),
        messageId: message.id,
        albumIndex: 0,
        url,
        name: message.media?.name || message.meta?.name || hints.name || 'Tệp đính kèm',
        mime,
        type: isImage(url, mime) ? 'image' : isVideo(url, mime) ? 'video' : isPDF(url, mime) ? 'pdf' : 'file',
        sender: message.fromName || message.sender_name || '',
        date: message.created_at || message.time || ''
      }
    ];
  });
  $: current = entries[index];
  $: currentMessage = (Array.isArray(messages) ? messages : []).find(
    (message) => String(message.id) === String(current?.messageId)
  );
  $: liked =
    likeOverrides[String(current?.messageId)] ??
    (currentMessage?.myReaction || currentMessage?.userReaction || currentMessage?.currentReaction) === '👍';
  $: downloadUrl = String(normalizeUrl(current?.url) || '').replace(
    /(\/api\/upload\/files\/[^/]+)\/content(?=[?#]|$)/,
    '$1/download'
  );
  $: if (index >= entries.length && entries.length) index = entries.length - 1;
  $: if (strip && current) {
    tick().then(() =>
      strip?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest', inline: 'center' })
    );
  }

  function select(next) {
    if (next < 0 || next >= entries.length) return;
    index = next;
    resetTransform();
  }

  function resetTransform() {
    scale = 1;
    rotation = 0;
    rotationFit = 1;
    panX = 0;
    panY = 0;
    activePointers.clear();
    pinchDistance = 0;
    zoomFocusPoint = null;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function clampPan() {
    if (!imageEl || !stageEl) return;
    const sideways = rotation % 180 !== 0;
    const width = (sideways ? imageEl.offsetHeight : imageEl.offsetWidth) * rotationFit * scale;
    const height = (sideways ? imageEl.offsetWidth : imageEl.offsetHeight) * rotationFit * scale;
    panX = clamp(panX, -Math.max(0, (width - stageEl.clientWidth) / 2), Math.max(0, (width - stageEl.clientWidth) / 2));
    panY = clamp(
      panY,
      -Math.max(0, (height - stageEl.clientHeight) / 2),
      Math.max(0, (height - stageEl.clientHeight) / 2)
    );
  }

  function rememberZoomFocus(clientX, clientY) {
    if (!stageEl || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
    zoomFocusPoint = { x: clientX, y: clientY };
  }

  function getZoomFocusPoint() {
    if (zoomFocusPoint) return zoomFocusPoint;
    if (!stageEl) return {};
    const box = stageEl.getBoundingClientRect();
    return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
  }

  function setScale(next, clientX, clientY) {
    if (current?.type !== 'image') return;
    const focus =
      Number.isFinite(clientX) && Number.isFinite(clientY) ? { x: clientX, y: clientY } : getZoomFocusPoint();
    const newScale = clamp(next, MIN_SCALE, MAX_SCALE);
    if (newScale === scale) return;
    if (stageEl && Number.isFinite(focus.x) && Number.isFinite(focus.y)) {
      const box = stageEl.getBoundingClientRect();
      const x = focus.x - box.left - box.width / 2;
      const y = focus.y - box.top - box.height / 2;
      const ratio = newScale / scale;
      panX = x - (x - panX) * ratio;
      panY = y - (y - panY) * ratio;
    }
    scale = newScale;
    if (scale === 1) {
      panX = 0;
      panY = 0;
    }
    clampPan();
  }

  function rotate() {
    if (current?.type !== 'image') return;
    rotation = (rotation + 90) % 360;
    updateRotationFit();
    panX = 0;
    panY = 0;
    clampPan();
  }

  function updateRotationFit() {
    if (!imageEl || !stageEl) return;
    if (!imageEl.offsetWidth || !imageEl.offsetHeight) return;
    rotationFit =
      rotation % 180 === 0
        ? 1
        : Math.min(1, stageEl.clientWidth / imageEl.offsetHeight, stageEl.clientHeight / imageEl.offsetWidth);
    clampPan();
  }

  async function togglePanel() {
    panelVisible = !panelVisible;
    await tick();
    updateRotationFit();
  }

  function wheel(event) {
    if (current?.type !== 'image') return;
    event.preventDefault();
    rememberZoomFocus(event.clientX, event.clientY);
    setScale(scale * Math.exp(-event.deltaY * 0.0015), event.clientX, event.clientY);
  }

  function pointerDown(event) {
    if (current?.type !== 'image') return;
    event.preventDefault();
    rememberZoomFocus(event.clientX, event.clientY);
    stageEl?.setPointerCapture?.(event.pointerId);
    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    lastPointer = { x: event.clientX, y: event.clientY };
    if (activePointers.size === 2) {
      const [a, b] = [...activePointers.values()];
      pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
      pinchScale = scale;
    }
  }

  function pointerMove(event) {
    if (current?.type !== 'image') return;
    event.preventDefault();
    rememberZoomFocus(event.clientX, event.clientY);
    if (!activePointers.has(event.pointerId)) return;
    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (activePointers.size >= 2) {
      const [a, b] = [...activePointers.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDistance > 0) setScale((pinchScale * distance) / pinchDistance, (a.x + b.x) / 2, (a.y + b.y) / 2);
    } else if (lastPointer && scale > 1) {
      panX += event.clientX - lastPointer.x;
      panY += event.clientY - lastPointer.y;
      clampPan();
    }
    lastPointer = { x: event.clientX, y: event.clientY };
  }

  function pointerUp(event) {
    activePointers.delete(event.pointerId);
    pinchDistance = 0;
    lastPointer = activePointers.size ? [...activePointers.values()][0] : null;
  }

  function showNotice(message) {
    notice = message;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => (notice = ''), 2400);
  }

  async function shareCurrent() {
    if (!current?.url) return;
    const url = new URL(normalizeUrl(current.url), window.location.href).href;
    try {
      if (navigator.share) await navigator.share({ title: current.name, url });
      else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        showNotice('Đã sao chép liên kết');
      } else showNotice('Thiết bị không hỗ trợ chia sẻ');
    } catch (error) {
      if (error?.name !== 'AbortError') showNotice('Không thể chia sẻ tệp');
    }
  }

  function toggleLike() {
    if (!current?.messageId) return;
    const next = !liked;
    likeOverrides = { ...likeOverrides, [String(current.messageId)]: next };
    sendMessage({ type: 'reaction', messageId: current.messageId, reaction: '👍', remove: !next });
  }

  function keydown(event) {
    if (event.key === 'Escape') onClose();
    if (event.target instanceof HTMLElement && ['INPUT', 'TEXTAREA'].includes(event.target.tagName)) return;
    if (event.key === 'ArrowLeft') select(index - 1);
    if (event.key === 'ArrowRight') select(index + 1);
    if (event.key === '+' || event.key === '=') setScale(scale + 0.25);
    if (event.key === '-') setScale(scale - 0.25);
  }

  onMount(() => {
    const selected = entries.findIndex(
      (entry) => String(entry.messageId) === String(initialMessageId) && entry.albumIndex === initialAlbumIndex
    );
    index = Math.max(0, selected);
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', keydown);
    window.addEventListener('resize', updateRotationFit);
  });

  onDestroy(() => {
    document.body.style.overflow = previousOverflow;
    window.removeEventListener('keydown', keydown);
    window.removeEventListener('resize', updateRotationFit);
    clearTimeout(noticeTimer);
  });
</script>

<Portal>
  <div
    class="media-viewer"
    class:panel-hidden={!panelVisible}
    role="dialog"
    aria-modal="true"
    aria-label="Xem tệp trong cuộc trò chuyện">
    <header class="toolbar">
      <button type="button" aria-label="Đóng" on:click={onClose}><X size={22} /></button>
      <div class="identity">
        <strong>{current?.sender || 'Tệp trong cuộc trò chuyện'}</strong>
        <span>
          {current?.date ? new Date(current.date).toLocaleString('vi-VN') : ''} · {index + 1}/{entries.length}
        </span>
      </div>
    </header>

    <main
      bind:this={stageEl}
      class="stage"
      on:pointerdown={pointerDown}
      on:pointermove={pointerMove}
      on:pointerup={pointerUp}
      on:pointercancel={pointerUp}>
      {#if current}
        {#key current.key}
          {#if current.type === 'image'}
            <img
              bind:this={imageEl}
              class="full-image"
              src={current.url}
              alt={current.name}
              draggable="false"
              style:transform={`translate3d(${panX}px, ${panY}px, 0) rotate(${rotation}deg) scale(${rotationFit * scale})`}
              on:wheel={wheel}
              on:dblclick={() => setScale(scale > 1 ? 1 : 2)}
              on:load={updateRotationFit} />
          {:else if current.type === 'video'}
            <VideoPreview src={current.url} name={current.name} mime={current.mime} mode="modal" />
          {:else if current.type === 'pdf'}
            <iframe title={current.name} src={current.url} style="width: 100vw;"></iframe>
          {:else}
            <div class="file-card">
              <FileText size={46} />
              <strong>{current.name}</strong>
              <a href={normalizeUrl(current.url)} target="_blank" rel="noopener noreferrer">Mở tệp</a>
            </div>
          {/if}
        {/key}
      {/if}
      {#if index > 0}<button class="arrow prev" type="button" aria-label="Tệp trước" on:click={() => select(index - 1)}>
          <ChevronLeft size={25} />
        </button>{/if}
      {#if index < entries.length - 1}<button
          class="arrow next"
          type="button"
          aria-label="Tệp sau"
          on:click={() => select(index + 1)}>
          <ChevronRight size={25} />
        </button>{/if}
    </main>

    {#if panelVisible && entries.length > 1}
      <nav bind:this={strip} class="strip" aria-label="Danh sách tệp">
        {#each entries as entry, i (entry.key)}
          <button
            type="button"
            class="thumb"
            aria-current={i === index ? 'true' : undefined}
            aria-label={`Xem tệp ${i + 1}: ${entry.name}`}
            on:click={() => select(i)}>
            {#if entry.type === 'image'}<img src={entry.url} alt="" loading="lazy" decoding="async" />
            {:else if entry.type === 'video'}<Video size={22} /><Play size={12} />
            {:else}<FileText size={22} />{/if}
          </button>
        {/each}
      </nav>
    {/if}
    <footer class="actionbar">
      <button type="button" aria-label="Chia sẻ" title="Chia sẻ" on:click={shareCurrent}><Share2 size={20} /></button>
      {#if current}<a href={downloadUrl} download={current.name} aria-label="Tải về" title="Tải về">
          <Download size={20} />
        </a>{/if}
      <span class="separator"></span>
      <button
        type="button"
        aria-label="Xoay ảnh"
        title="Xoay ảnh"
        disabled={current?.type !== 'image'}
        on:click={rotate}>
        <RotateCw size={20} />
      </button>
      <button
        type="button"
        aria-label="Phóng to"
        title="Phóng to"
        disabled={current?.type !== 'image' || scale >= MAX_SCALE}
        on:click={() => setScale(scale + 0.4)}>
        <ZoomIn size={20} />
      </button>
      <button
        type="button"
        aria-label="Thu nhỏ"
        title="Thu nhỏ"
        disabled={current?.type !== 'image' || scale <= MIN_SCALE}
        on:click={() => setScale(scale - 0.4)}>
        <ZoomOut size={20} />
      </button>
      <span class="separator"></span>
      <button type="button" aria-label="Thích" title="Thích" aria-pressed={liked} class:liked on:click={toggleLike}>
        <Heart size={20} fill={liked ? 'currentColor' : 'none'} />
      </button>
      <button
        type="button"
        aria-label={panelVisible ? 'Ẩn danh sách tệp' : 'Hiện danh sách tệp'}
        title={panelVisible ? 'Ẩn danh sách tệp' : 'Hiện danh sách tệp'}
        aria-pressed={panelVisible}
        on:click={togglePanel}>
        {#if panelVisible}<PanelRightClose size={20} />{:else}<PanelRightOpen size={20} />{/if}
      </button>
    </footer>
    {#if notice}<div class="notice" role="status">{notice}</div>{/if}
  </div>
</Portal>

<style>
  .media-viewer {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 6.5rem;
    grid-template-rows: auto minmax(0, 1fr) auto;
    color: white;
    background: #0b0b0c;
  }
  .media-viewer.panel-hidden {
    grid-template-columns: minmax(0, 1fr);
  }
  .toolbar {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 1rem;
    min-height: 3.75rem;
    padding: 0.5rem max(1rem, env(safe-area-inset-right));
    background: #171719;
  }
  .toolbar button {
    display: grid;
    place-items: center;
    width: 2.5rem;
    height: 2.5rem;
    flex: none;
    color: inherit;
    border: 0;
    border-radius: 0.5rem;
    background: transparent;
  }
  .identity {
    display: grid;
    min-width: 0;
    flex: 1;
  }
  .identity strong,
  .identity span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .identity span {
    color: #aaa;
    font-size: 0.75rem;
  }
  .stage {
    position: relative;
    display: grid;
    min-height: 0;
    place-items: center;
    overflow: hidden;
    /* padding: 0.6rem 3.5rem; */
    touch-action: none;
    overscroll-behavior: contain;
  }
  .full-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: none;
    will-change: transform;
    cursor: grab;
  }
  .full-image:active {
    cursor: grabbing;
  }
  .stage :global(.video-shell) {
    max-height: 100%;
  }
  iframe {
    width: min(100%, 70rem);
    height: 100%;
    border: 0;
    background: white;
  }
  .file-card {
    display: grid;
    justify-items: center;
    gap: 1rem;
    max-width: 22rem;
    text-align: center;
    overflow-wrap: anywhere;
  }
  .file-card a {
    padding: 0.6rem 1rem;
    border-radius: 0.5rem;
    color: #111;
    background: white;
  }
  .arrow {
    position: absolute;
    top: 50%;
    display: grid;
    place-items: center;
    width: 2.7rem;
    height: 2.7rem;
    border: 0;
    border-radius: 50%;
    color: white;
    background: #27272acc;
    transform: translateY(-50%);
  }
  .prev {
    left: 0.55rem;
  }
  .next {
    right: 0.55rem;
  }
  .strip {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    min-height: 0;
    overflow-y: auto;
    padding: 0.55rem;
    background: #171719;
    scrollbar-width: thin;
  }
  .thumb {
    display: grid;
    flex: 0 0 3.7rem;
    width: 3.7rem;
    height: 3.7rem;
    place-items: center;
    overflow: hidden;
    border: 2px solid transparent;
    border-radius: 0.4rem;
    color: #ddd;
    background: #303035;
  }
  .thumb[aria-current='true'] {
    border-color: #38bdf8;
  }
  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .actionbar {
    grid-column: 1 / -1;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    min-height: 3.25rem;
    padding: 0.35rem 0.6rem max(0.35rem, env(safe-area-inset-bottom));
    background: #262628;
  }
  .actionbar button,
  .actionbar a {
    display: grid;
    flex: none;
    place-items: center;
    width: 2.7rem;
    height: 2.7rem;
    border: 0;
    border-radius: 0.5rem;
    color: #f5f5f5;
    background: transparent;
  }
  .actionbar button:hover,
  .actionbar a:hover,
  .toolbar button:hover {
    background: #444448;
  }
  .actionbar button:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .actionbar button.liked {
    color: #fb7185;
  }
  .separator {
    height: 1.4rem;
    border-left: 1px solid #777;
    margin: 0 0.25rem;
  }
  .notice {
    position: absolute;
    bottom: 4rem;
    left: 50%;
    padding: 0.5rem 0.8rem;
    border-radius: 0.5rem;
    background: #303033;
    font-size: 0.8rem;
    transform: translateX(-50%);
  }
  @media (max-width: 640px) {
    .media-viewer {
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: auto minmax(0, 1fr) auto auto;
    }
    .toolbar {
      padding-top: max(0.5rem, env(safe-area-inset-top));
    }
    .stage {
      padding: 0.4rem;
    }
    .arrow {
      top: auto;
      bottom: 0.4rem;
      transform: none;
    }
    .strip {
      flex-direction: row;
      align-items: center;
      min-height: 5rem;
      max-width: 100vw;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 0.5rem 1rem;
    }
    .actionbar {
      justify-content: space-between;
      gap: 0;
    }
    .actionbar button,
    .actionbar a {
      width: min(12vw, 2.7rem);
    }
  }
</style>
